import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import debounce from 'lodash.debounce'
import { Button, Stack } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useCredentialedDataProviderOrgs from '@/hooks/useCredentialedDataProviderOrgs'
import { OverlayState } from '@/hooks/useOverlay'
import { useUpdateVendorExclusions } from '@/service-library/hooks/vendor-exclusions'
import { useGetVendorsLiveList } from '@/services/hooks/relay/live-lists'
import { VendorLiveListEntity } from '@/types/live-lists'
import { Organization } from '@/types/organizations'
import { Project } from '@/types/projects'
import { VendorExclusion } from '@/types/rollup'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormAutocomplete, PixyDocsForm } from '@/components/forms'
import { useInfiniteScrollListbox } from '@/components/validation/fields/InfiniteScrollListBox'
import getVendorNum from './getVendorNum'

// FormAutocomplete options must carry an `id`; live-list entities key on
// `entity_id` instead, so options are mapped into this shape.
type VendorOption = VendorLiveListEntity & { id: string }

type FormValues = {
  company: Organization | null
  vendors: VendorOption[]
}

const defaultValues: FormValues = {
  company: null,
  vendors: [],
}

type ExcludeVendorsDialogProps = {
  overlay: OverlayState
  project: Project
  dataProviderOrgs: Organization[]
  vendorExclusions: VendorExclusion[]
  vendorExclusionsQueryKey: QueryKey
}

export default function ExcludeVendorsDialog({
  overlay,
  project,
  dataProviderOrgs,
  vendorExclusions,
  vendorExclusionsQueryKey,
}: ExcludeVendorsDialogProps) {
  const [inputValue, setInputValue] = useState('')
  const [searchValue, setSearchValue] = useState('')

  const methods = useForm<FormValues>({ defaultValues })
  const { reset, setValue, watch } = methods
  const company = watch('company')
  const selectedVendors = watch('vendors')

  const hasOrgCredentialType = !!project.org_credential_type_id

  const {
    credentialedDataProviderOrgs: companyOptions,
    isLoading: isLoadingCompanyOptions,
  } = useCredentialedDataProviderOrgs({
    project,
    dataProviderOrgs,
    enabled: overlay.isOpen,
  })

  // With a single qualifying company there's nothing to choose — preselect it.
  useEffect(() => {
    if (overlay.isOpen && !company && companyOptions.length === 1) {
      setValue('company', companyOptions[0])
    }
  }, [company, companyOptions, overlay.isOpen, setValue])

  const excludedVendorNumbersForCompany = useMemo(() => {
    if (!company) return new Set<string>()
    return new Set(
      vendorExclusions
        .filter((exclusion) => exclusion.dp_org_id === company.id)
        .map((exclusion) => exclusion.vendor_num),
    )
  }, [company, vendorExclusions])

  const {
    allData: vendors,
    hasNextPage,
    fetchNextPage,
    isFetching: isFetchingVendors,
  } = useGetVendorsLiveList({
    orgId: company?.id || '',
    orgCredentialTypeId: project.org_credential_type_id || '',
    filters: {
      query: searchValue,
      fields__only: 'entity_id,pd_display_name,external_vendor_id',
      limit: '20',
    },
    enabled: overlay.isOpen && !!company && hasOrgCredentialType,
    placeholderData: (previousData) => previousData,
  })

  const vendorOptions = useMemo<VendorOption[]>(
    () => vendors.map((vendor) => ({ ...vendor, id: vendor.entity_id })),
    [vendors],
  )

  const debouncedSearch = useMemo(
    () => debounce((newValue: string) => setSearchValue(newValue), 200),
    [],
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const { listboxSlotProps } = useInfiniteScrollListbox({
    hasNextPage,
    fetchNextPage,
    isFetching: isFetchingVendors,
    optionsLength: vendorOptions.length,
  })

  const resetState = () => {
    reset(defaultValues)
    setInputValue('')
    setSearchValue('')
    debouncedSearch.cancel()
  }

  const { updateVendorExclusions } = useUpdateVendorExclusions({
    listQueryKey: vendorExclusionsQueryKey,
    onError: () => {
      // Rows are reported individually, so a failure here can mean one vendor
      // or all of them — don't claim more than we know.
      showErrorSnackbar(
        'One or more vendors could not be excluded. Please try again later.',
      )
    },
  })

  const handleSubmit = ({ company, vendors }: FormValues) => {
    if (!company || vendors.length === 0) return
    overlay.close()
    updateVendorExclusions(
      vendors.map((vendor) => ({
        id: generateUuid(),
        project_id: project.id,
        dp_org_id: company.id,
        vendor_num: getVendorNum(vendor),
        display_name: vendor.pd_display_name,
      })),
    )
  }

  const submitLabel =
    selectedVendors.length > 1
      ? `Exclude ${selectedVendors.length} vendors`
      : 'Exclude vendor'

  return (
    <Dialog
      maxWidth="sm"
      {...overlay}
      onClose={resetState}
      title="Exclude Vendors"
      subtitle="Line item rollup will be skipped entirely for these vendors."
    >
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3}>
            <FormAutocomplete<FormValues, Organization>
              name="company"
              label="Company"
              options={companyOptions}
              loading={isLoadingCompanyOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={() => {
                // Changing the company invalidates any vendors picked for the
                // previous one.
                setValue('vendors', [])
              }}
              textFieldProps={{ placeholder: 'Select a company' }}
            />

            <FormAutocomplete<FormValues, VendorOption>
              name="vendors"
              label="Vendors"
              multiple
              disabled={!company || !hasOrgCredentialType}
              options={vendorOptions}
              loading={isFetchingVendors}
              filterOptions={(options) => options}
              getOptionLabel={(option) => option.pd_display_name || ''}
              isOptionEqualToValue={(option, selected) =>
                option.id === selected.id
              }
              getOptionDisabled={(option) =>
                excludedVendorNumbersForCompany.has(getVendorNum(option))
              }
              inputValue={inputValue}
              onInputChange={(_e, newInputValue, reason) => {
                if (reason === 'input') {
                  setInputValue(newInputValue)
                  debouncedSearch(newInputValue)
                } else {
                  setInputValue('')
                  debouncedSearch.cancel()
                  setSearchValue('')
                }
              }}
              helperText={
                hasOrgCredentialType
                  ? undefined
                  : 'This project has no export integration configured.'
              }
              textFieldProps={{ placeholder: 'Search vendors' }}
              slotProps={{ listbox: listboxSlotProps }}
            />
          </Stack>
        </DialogContent>

        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!company || selectedVendors.length === 0}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
